import { Avatar } from 'components/Avatar';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { ModalScreens } from 'components/Navigation/types';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { Txt } from 'components/Txt';
import equal from 'fast-deep-equal';
import { useSelector } from 'hooks/useSelector';
import { getOptionFieldResultDisplayValues, hasValue } from 'lib/fields';
import { pluralise } from 'lib/pluralise';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { APIRemoteUrl } from 'service/api/constants';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doAseLightFieldInstanceDelete,
  doAseLightFieldInstanceUndelete,
} from 'store/assessments/actions';
import {
  selectAseFieldInstanceAseMerged,
  selectAseFieldInstanceAst,
  selectAseTplFieldGroup,
  selectAseTemplateFieldFieldsConfig,
  selectAseTplFieldToGroup,
  selectAseFieldInstanceChildInstanceIsInParentAst,
  selectAseTempDataInstanceAseMerged,
  selectAseDataFieldFields,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';
import { FieldTypes } from 'store/assets/types';

type Props = {
  instanceId: string;
  remoteImageUrl?: APIRemoteUrl;
  parent: NonNullable<InstanceParentParams>;
  isEditable: boolean;
  isDeleted: boolean;
  isViewable: boolean;
};

export const LightRepeatableListItem: React.FC<Props> = React.memo(
  ({
    instanceId,
    parent,
    remoteImageUrl,
    isEditable,
    isDeleted,
    isViewable,
  }) => {
    const dispatch = useDispatch();

    const assessmentInstance = useSelector(
      (state) => selectAseFieldInstanceAseMerged(state, instanceId),
      equal,
    );
    const tempAssessmentInstance = useSelector(
      (state) => selectAseTempDataInstanceAseMerged(state, instanceId),
      equal,
    );

    const finalAssessmentInstance =
      assessmentInstance || tempAssessmentInstance;

    const assetInstance = useSelector(
      (state) => selectAseFieldInstanceAst(state, instanceId),
      equal,
    );

    const groupId = finalAssessmentInstance?.groupId || assetInstance?.groupId;

    const fields = useSelector(selectAseDataFieldFields);
    const group = useSelector((state) =>
      selectAseTplFieldGroup(state, groupId || '-1'),
    );
    const parentGroup = useSelector((state) =>
      selectAseTplFieldToGroup(state, parent.fieldId),
    );

    const isInParentAst = useSelector((state) =>
      selectAseFieldInstanceChildInstanceIsInParentAst(
        state,
        parent,
        instanceId,
      ),
    );

    const fieldsConfig = useSelector(selectAseTemplateFieldFieldsConfig);

    if ((!assetInstance && !finalAssessmentInstance) || !group) {
      return null;
    }

    const instance = finalAssessmentInstance || assetInstance!;

    const { imageFieldId } = group.calculatedInfo;

    const imageId: string | undefined = imageFieldId
      ? instance.results[imageFieldId]?.value?.[0]
      : undefined;

    const resultsCount = Object.values(instance.results).filter(
      hasValue,
    ).length;

    const additionalTitles = group.fieldIds.reduce<string[]>((acc, fieldId) => {
      // Field must be included in fieldsConfig for the user to see it in this assessment
      if (fieldsConfig[fieldId]) {
        const field = fields[fieldId]!;
        const result = instance.results[fieldId];
        if (result) {
          if (field.type === FieldTypes.OPTION) {
            const optionValues = getOptionFieldResultDisplayValues(
              field,
              result,
              true,
            ).join(', ');
            if (
              optionValues &&
              optionValues !== 'Yes' &&
              optionValues !== 'No'
            ) {
              acc.push(optionValues);
            }
          } else if (
            field.type === FieldTypes.TEXT ||
            field.type === FieldTypes.NOTE
          ) {
            if (result.value?.length) {
              acc.push(result.value as string);
            }
          }
        }
      }
      return acc;
    }, []);

    const handlePress = () => {
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.AseLightRepeatableInstance,
          params: {
            groupId: group.id,
            instanceId,
            parent,
          },
        }),
      );
    };

    const isAdded = parentGroup && !isInParentAst;
    const resolvedRemoteImageUrl = remoteImageUrl?.(imageId);

    return (
      <CardRow
        onPress={isDeleted ? undefined : isViewable ? handlePress : undefined}
        showMenuOnPress={isDeleted}
        menuOptions={
          isEditable
            ? isDeleted
              ? [
                  {
                    text: 'Undelete',
                    onPress: () =>
                      dispatch(
                        doAseLightFieldInstanceUndelete({
                          instanceId,
                          parent,
                        }),
                      ),
                    icon: ICONS.TrashCanXmarkLight,
                  },
                ].filter(filterArrayEmpty)
              : [
                  {
                    text: 'Delete From RiskBase',
                    onPress: () =>
                      dispatch(
                        doAseLightFieldInstanceDelete({
                          instanceId,
                          parent,
                        }),
                      ),
                    icon: ICONS.TrashCanLight,
                    destructive: true,
                  },
                ].filter(filterArrayEmpty)
            : undefined
        }
        indentActions
      >
        <View
          style={{
            alignItems: 'center',
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          {!!imageFieldId && (
            <View style={{ marginRight: theme.padding, width: 52 }}>
              {remoteImageUrl ? (
                <Avatar
                  data={
                    resolvedRemoteImageUrl
                      ? { remoteUrl: resolvedRemoteImageUrl }
                      : null
                  }
                  size={52}
                />
              ) : (
                <LocalThumbnail
                  thumbnailId={imageId}
                  width={52}
                  height={52}
                  rounded
                />
              )}
            </View>
          )}
          <ItemDetails
            titleStyledAsCardLabel
            title={additionalTitles.join(' • ') || 'No Value'}
            titleStyle={
              isDeleted
                ? {
                    textDecorationLine: 'line-through',
                    textDecorationColor: theme.colors.red,
                  }
                : undefined
            }
            subtitle={
              <>
                <Txt size="xsmall">
                  {pluralise(
                    resultsCount,
                    '1 Result',
                    '{x} Results',
                    'No Results',
                  )}
                </Txt>
                {isEditable && isDeleted && (
                  <>
                    <Txt size="xsmall"> • </Txt>
                    <Txt size="xsmall" style={{ color: theme.colors.red }}>
                      Deleted
                    </Txt>
                  </>
                )}
                {isEditable && isAdded && (
                  <>
                    <Txt size="xsmall"> • </Txt>
                    <Txt size="xsmall" style={{ color: theme.colors.green }}>
                      New
                    </Txt>
                  </>
                )}
              </>
            }
          />
        </View>
      </CardRow>
    );
  },
);
